import dayjs from 'dayjs'
import { MOMENT_FORMATS } from 'src/consumer/constants'

/**
 * Calculate the earliest start time for a booked slot based on clinic schedule
 * @param {Object} bookedSlot - The booked slot data
 * @param {Object} clinicLocationData - Clinic location and schedule data
 * @returns {string} Formatted time string
 */
export const calculateCreatedTimeFrom = (bookedSlot, clinicLocationData) => {
  return dayjs(bookedSlot?.startTime).startOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  if (!bookedSlot?.startTime || !clinicLocationData?.schedules) {
    // Fallback to start of day if no schedule data
  }

  const defaultSchedule = getDefaultSchedule(clinicLocationData.schedules)
  if (!defaultSchedule?.weeklyHourMap) {
    // Fallback to start of day if no weekly schedule
    return dayjs(bookedSlot?.startTime).startOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  }

  const daySchedule = getDaySchedule(bookedSlot?.startTime, defaultSchedule.weeklyHourMap)
  if (!daySchedule?.available || !daySchedule?.slotList?.length) {
    // Fallback to start of day if day is not available
    return dayjs(bookedSlot?.startTime).startOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  }

  const earliestStartTime = getEarliestStartTime(daySchedule.slotList)
  if (!earliestStartTime) {
    // Fallback to start of day if no valid start time
    return dayjs(bookedSlot?.startTime).startOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  }

  // Combine the date from bookedSlot with the earliest start time from schedule
  const dateOnly = dayjs(bookedSlot?.startTime).format('YYYY-MM-DD')
  const combinedDateTime = dayjs(`${dateOnly} ${earliestStartTime}`)

  return combinedDateTime.format(MOMENT_FORMATS.javaInstantFormat)
}

/**
 * Calculate the latest end time for a booked slot based on clinic schedule
 * @param {Object} bookedSlot - The booked slot data
 * @param {Object} clinicLocationData - Clinic location and schedule data
 * @returns {string} Formatted time string
 */
export const calculateCreatedTimeTo = (bookedSlot, clinicLocationData) => {
  return dayjs(bookedSlot?.endTime).endOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  if (!bookedSlot?.endTime || !clinicLocationData?.schedules) {
    // Fallback to end of day if no schedule data
  }

  const defaultSchedule = getDefaultSchedule(clinicLocationData.schedules)
  if (!defaultSchedule?.weeklyHourMap) {
    // Fallback to end of day if no weekly schedule
    return dayjs(bookedSlot?.endTime).endOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  }

  const daySchedule = getDaySchedule(bookedSlot?.endTime, defaultSchedule.weeklyHourMap)
  if (!daySchedule?.available || !daySchedule?.slotList?.length) {
    // Fallback to end of day if day is not available
    return dayjs(bookedSlot?.endTime).endOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  }

  const latestEndTime = getLatestEndTime(daySchedule.slotList)
  if (!latestEndTime) {
    // Fallback to end of day if no valid end time
    return dayjs(bookedSlot?.endTime).endOf('day').format(MOMENT_FORMATS.javaInstantFormat)
  }

  // Combine the date from bookedSlot with the latest end time from schedule
  const dateOnly = dayjs(bookedSlot?.endTime).format('YYYY-MM-DD')
  const combinedDateTime = dayjs(`${dateOnly} ${latestEndTime}`)

  return combinedDateTime.format(MOMENT_FORMATS.javaInstantFormat)
}

// Helper functions
const getDefaultSchedule = (schedules) => {
  return schedules.find((schedule) => schedule.defaultSchedule) || schedules[0]
}

const getDaySchedule = (dateTime, weeklyHourMap) => {
  const dayOfWeek = dayjs(dateTime).format('dddd').toLowerCase()
  return weeklyHourMap[dayOfWeek]
}

const getEarliestStartTime = (slotList) => {
  return slotList.reduce((earliest, slot) => {
    if (!earliest || slot.startTime < earliest) {
      return slot.startTime
    }
    return earliest
  }, null)
}

const getLatestEndTime = (slotList) => {
  return slotList.reduce((latest, slot) => {
    if (!latest || slot.endTime > latest) {
      return slot.endTime
    }
    return latest
  }, null)
}
